import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, Clock3, Flame, Sparkles, XCircle } from 'lucide-react';
import {
  abandonSpeedMatchRun,
  answerSpeedMatchPair,
  completeSpeedMatchRun,
  createSpeedMatchRun,
  getSpeedMatchRun,
  type SpeedMatchComplete,
  type SpeedMatchGrade,
  type SpeedMatchRun,
  type SpeedMatchRunState,
} from '../api/learning';
import { ArcadeSummaryView } from '../components/ui';
import './SpeedMatchGrid.css';

type PendingPair = { questionId: string; answer: string };
/** Create returns SpeedMatchRun; resume returns SpeedMatchRunState — both share board fields. */
type SpeedMatchBoard = SpeedMatchRun | SpeedMatchRunState;

function secondsUntil(deadlineAt: string | null): number | null {
  if (!deadlineAt) return null;
  return Math.max(0, Math.ceil((Date.parse(deadlineAt) - Date.now()) / 1000));
}

function formatTime(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function SpeedMatchGrid() {
  const [relaxed, setRelaxed] = useState(false);
  const [run, setRun] = useState<SpeedMatchBoard | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
  const [resolvedQuestionIds, setResolvedQuestionIds] = useState<string[]>([]);
  const [questionOutcomes, setQuestionOutcomes] = useState<Record<string, boolean>>({});
  const [resolvedAnswers, setResolvedAnswers] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingPair | null>(null);
  const [grade, setGrade] = useState<SpeedMatchGrade | null>(null);
  const [summary, setSummary] = useState<SpeedMatchComplete | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'start' | 'grade' | 'recover' | null>(null);
  const finalizingRef = useRef(false);

  const translations = useMemo(
    () => [...new Set(run?.questions.flatMap(question => question.options) ?? [])],
    [run?.questions]
  );

  useEffect(() => {
    if (!run || run.timing === 'relaxed' || !run.deadlineAt || summary) {
      setRemainingSeconds(null);
      return;
    }
    const update = () => setRemainingSeconds(secondsUntil(run.deadlineAt));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [run, summary]);

  useEffect(() => {
    if (remainingSeconds !== 0 || !run || pending || finalizingRef.current) return;
    let cancelled = false;
    finalizingRef.current = true;
    setBusy(true);
    completeSpeedMatchRun(run.runId)
      .then(result => {
        if (!cancelled) setSummary(result);
      })
      .catch(() => {
        if (!cancelled) setError('recover');
      })
      .finally(() => {
        finalizingRef.current = false;
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pending, remainingSeconds, run]);

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const created = await createSpeedMatchRun(relaxed ? 'relaxed' : 'standard');
      setRun(created);
      setRemainingSeconds(secondsUntil(created.deadlineAt));
      setSelectedQuestionId(null);
      setResolvedQuestionIds([]);
      setQuestionOutcomes({});
      setResolvedAnswers([]);
      setPending(null);
      setGrade(null);
      setSummary(null);
    } catch {
      setError('start');
    } finally {
      setBusy(false);
    }
  }

  async function submitPair(questionId: string, answer: string) {
    if (!run || busy || resolvedQuestionIds.includes(questionId)) return;
    const nextPair = { questionId, answer };
    setPending(nextPair);
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerSpeedMatchPair(run.runId, questionId, answer));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  function applyGrade(nextGrade: SpeedMatchGrade) {
    setRun(current =>
      current
        ? {
            ...current,
            score: nextGrade.score,
            combo: nextGrade.combo,
            bestCombo: nextGrade.bestCombo,
            deadlineAt: nextGrade.deadlineAt,
            status: nextGrade.status,
            personalBest: nextGrade.personalBest,
          }
        : current
    );
    setResolvedQuestionIds(current =>
      current.includes(nextGrade.questionId) ? current : [...current, nextGrade.questionId]
    );
    setQuestionOutcomes(current => ({ ...current, [nextGrade.questionId]: nextGrade.correct }));
    setResolvedAnswers(current =>
      current.includes(nextGrade.correctAnswer) ? current : [...current, nextGrade.correctAnswer]
    );
    setGrade(nextGrade);
    setPending(null);
    setSelectedQuestionId(null);
    setError(null);
    if (nextGrade.complete && nextGrade.verifiedScore !== null) {
      setSummary({
        runId: run?.runId ?? '',
        status: nextGrade.status,
        verifiedScore: nextGrade.verifiedScore,
        bestCombo: nextGrade.bestCombo,
        correctCount: nextGrade.correctCount,
        answeredCount: nextGrade.answeredCount,
        accuracy: nextGrade.accuracy,
        personalBest: nextGrade.personalBest ?? nextGrade.verifiedScore,
        isNewPersonalBest: nextGrade.isNewPersonalBest,
      });
    }
  }

  async function retry() {
    if (!run || !pending) return;
    setBusy(true);
    setError(null);
    try {
      applyGrade(await answerSpeedMatchPair(run.runId, pending.questionId, pending.answer));
    } catch {
      setError('grade');
    } finally {
      setBusy(false);
    }
  }

  async function resume() {
    if (!run) return;
    setBusy(true);
    setError(null);
    try {
      const recovered = await getSpeedMatchRun(run.runId);
      setRun(recovered);
      setResolvedQuestionIds(recovered.gradedQuestionIds);
      setPending(null);
      if (recovered.status !== 'in_progress' && recovered.verifiedScore !== null) {
        setSummary({
          runId: recovered.runId,
          status: recovered.status,
          verifiedScore: recovered.verifiedScore,
          bestCombo: recovered.bestCombo,
          correctCount: recovered.correctCount,
          answeredCount: recovered.answeredCount,
          accuracy: recovered.accuracy,
          personalBest: recovered.personalBest ?? recovered.verifiedScore,
          isNewPersonalBest: recovered.isNewPersonalBest,
        });
      }
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  async function abandon() {
    if (!run) return;
    setBusy(true);
    try {
      await abandonSpeedMatchRun(run.runId);
      setRun(null);
      setPending(null);
      setGrade(null);
      setError(null);
    } catch {
      setError('recover');
    } finally {
      setBusy(false);
    }
  }

  if (summary) {
    return (
      <ArcadeSummaryView
        heading="Board complete"
        verifiedScore={summary.verifiedScore}
        accuracy={summary.accuracy}
        correctCount={summary.correctCount}
        answeredCount={summary.answeredCount}
        isNewPersonalBest={summary.isNewPersonalBest}
        additionalStats={[{ label: 'Best combo', value: `×${summary.bestCombo}` }]}
        playAgainDisabled={busy}
        onPlayAgain={() => void start()}
      />
    );
  }

  if (!run) {
    return (
      <section aria-labelledby="speed-heading">
        <div className="learning-section-heading">
          <div>
            <h2 id="speed-heading">Speed Match</h2>
            <p>Pair every term with its translation. Complete the pairs in any order.</p>
          </div>
        </div>
        {error === 'start' && <SpeedError message="We could not start a Speed Match board." />}
        <div className="speed-start-card">
          <Clock3 aria-hidden="true" />
          <p>
            {relaxed ? 'No timer. Score still comes from the server.' : 'Race the board clock.'}
          </p>
          <label className="speed-toggle">
            <input
              type="checkbox"
              checked={relaxed}
              onChange={event => setRelaxed(event.target.checked)}
            />
            Relaxed — no timer
          </label>
          <button className="speed-primary" onClick={() => void start()} disabled={busy}>
            {busy ? 'Building board…' : 'Start Speed Match'}
          </button>
          {busy && (
            <span className="sr-only" role="status">
              Building Speed Match board…
            </span>
          )}
        </div>
      </section>
    );
  }

  if (run.totalQuestions === 0) {
    return (
      <section className="learning-empty" aria-labelledby="speed-empty-title">
        <Sparkles aria-hidden="true" />
        <h2 id="speed-empty-title">No vocabulary ready yet</h2>
        <p>Save translated words and phrases to create a Speed Match board.</p>
        <button className="speed-primary" onClick={() => void abandon()} disabled={busy}>
          Back to start
        </button>
        {/* This branch returns before the board's recovery alert, so a failed
            abandon here would otherwise strand the run with no feedback. */}
        {error === 'recover' && (
          <div className="speed-recovery" role="alert">
            <p>The run could not be ended. Try again.</p>
          </div>
        )}
      </section>
    );
  }

  return (
    <section aria-labelledby="speed-board-heading" aria-busy={busy}>
      <p className="sr-only" role="status">
        Speed Match board ready.
      </p>
      <div className="speed-hud">
        <div>
          <span>Score</span>
          <strong>{run.score.toLocaleString()}</strong>
        </div>
        <div>
          <span>Combo</span>
          <strong>
            <Flame aria-hidden="true" /> ×{run.combo}
          </strong>
        </div>
        <div>
          <span>Time</span>
          <strong>{remainingSeconds === null ? 'No timer' : formatTime(remainingSeconds)}</strong>
        </div>
      </div>
      <div className="learning-section-heading speed-board-heading">
        <div>
          <h2 id="speed-board-heading">Match the board</h2>
          <p>Select a term, then its translation. Completed pairs are marked with a check.</p>
        </div>
        <span className="learning-count">
          {resolvedQuestionIds.length} of {run.totalQuestions}
        </span>
      </div>

      {grade && (
        <div
          className={`speed-feedback speed-feedback--${grade.correct ? 'correct' : 'incorrect'}`}
          role="status"
          aria-live="polite"
        >
          {grade.correct ? <CheckCircle2 aria-hidden="true" /> : <XCircle aria-hidden="true" />}
          <span>
            {grade.correct
              ? `Matched · +${grade.scoreDelta} points · Combo ×${grade.combo}`
              : `Not a match · Correct translation: ${grade.correctAnswer}`}
          </span>
        </div>
      )}
      {error === 'grade' && pending && (
        <div className="speed-recovery" role="alert">
          <p>That pair was not confirmed. The board is paused at its last server state.</p>
          <button onClick={() => void retry()} disabled={busy}>
            Retry pair
          </button>
          <button onClick={() => void resume()} disabled={busy}>
            Resume from server
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}
      {error === 'recover' && (
        <div className="speed-recovery" role="alert">
          <p>The run could not be recovered.</p>
          <button onClick={() => void resume()} disabled={busy}>
            Try resume again
          </button>
          <button onClick={() => void abandon()} disabled={busy}>
            End unverified run
          </button>
        </div>
      )}

      <div className="speed-board" aria-label="Speed Match board">
        <div className="speed-column" aria-label="Terms">
          <h3>Terms</h3>
          {run.questions.map(question => {
            const resolved = resolvedQuestionIds.includes(question.id);
            const selected = selectedQuestionId === question.id;
            const outcome = questionOutcomes[question.id];
            return (
              <button
                key={question.id}
                className="speed-tile"
                aria-pressed={selected}
                disabled={resolved || busy || remainingSeconds === 0}
                onClick={() => setSelectedQuestionId(selected ? null : question.id)}
              >
                {outcome === true && (
                  <CheckCircle2 className="speed-outcome--correct" aria-hidden="true" />
                )}
                {outcome === false && (
                  <XCircle className="speed-outcome--incorrect" aria-hidden="true" />
                )}
                <span>{question.prompt}</span>
                {resolved && (
                  <span className="sr-only">
                    {outcome === true
                      ? 'Matched correctly'
                      : outcome === false
                        ? 'Answered incorrectly'
                        : 'Answered'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="speed-column" aria-label="Translations">
          <h3>Translations</h3>
          {translations.map(answer => {
            const resolved = resolvedAnswers.includes(answer);
            return (
              <button
                key={answer}
                className="speed-tile"
                disabled={resolved || busy || !selectedQuestionId || remainingSeconds === 0}
                onClick={() => selectedQuestionId && void submitPair(selectedQuestionId, answer)}
              >
                {resolved && <CheckCircle2 aria-hidden="true" />}
                <span>{answer}</span>
                {resolved && <span className="sr-only">Matched</span>}
              </button>
            );
          })}
        </div>
      </div>
      <button className="speed-abandon" onClick={() => void abandon()} disabled={busy}>
        End run without saving a score
      </button>
    </section>
  );
}

function SpeedError({ message }: { message: string }) {
  return (
    <div className="learning-notice learning-notice--error" role="alert">
      <XCircle aria-hidden="true" /> <span>{message}</span>
    </div>
  );
}
